import { useSelector } from 'react-redux';
import { startCase } from 'lodash';
import {
  Citations,
  OnboardingChrome,
  citations,
  LikelihoodSlideOutContent,
  LtcAtAgeSlideOutContent,
} from '../components';
import {
  selectAppliedInferenceSet,
  selectClient,
  selectInferenceSet,
  selectIntakeSurvey,
} from '../model';
import { isInferenceEdited, possessivePronoun } from '../util';
import moduleStyles from './OnboardingOurPredictions.module.css';
import mainStyles from '../styles/main.module.css';
import { SlideContent, SlideOutSheet } from '../components';

import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { Badge } from '../components/Badge';
import { cn } from '@client/lib';

const styles = {
  ...mainStyles,
  ...moduleStyles,
};

export function OnboardingOurPredictions(_props: ClientIdProps) {
  const {
    clientFirstName,
    appliedInferenceSet: { ltcAtAgeShowRange },
  } = useSelector(selectClient);

  return (
    <OnboardingChrome
      slidePage={OnboardingOurPredictions}
      nextButtonLabel={`${clientFirstName}'s Care Needs`}
    >
      <SlideContent headerText={`Our predictions for ${clientFirstName}`}>
        <div className="flex flex-col gap-4 pb-0 pt-4 lg:flex-row lg:gap-5 lg:pb-12 lg:pt-12">
          <ColumnLtcLikelihoodEver />
          <ColumnLtcAtAge />
          <ColumnLtcAgeFallBack />
        </div>
        <div className="py-4 md:py-16">
          <Citations
            citations={
              ltcAtAgeShowRange
                ? [
                    citations.onboardingOurPredictionsLikelihoodNationalAverage,
                    citations.onboardingOurPredictionsLtcAtAgeRange,
                  ]
                : [citations.onboardingOurPredictionsLikelihoodNationalAverage]
            }
          />
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

export function ColumnLtcLikelihoodEver() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  return (
    <div
      className="flex-grow-1 border-primary-200 flex basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predictions-likelihood-column"
    >
      <p
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predictions-likelihood-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} likelihood of needing LTC
        <SlideOutSheet
          slideOutContent={<LikelihoodSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="size-5 pb-1 pl-1 text-gray-500 hover:text-gray-700"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </p>
      <p
        className="text-primary-dark py-4 text-5xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predictions-likelihood-value"
      >
        {Math.round(ltcLikelihoodEver * 100)}
        <span className={cn(styles.percentSymbol, 'leading-none')}>%</span>
      </p>
      <p
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predictions-likelihood-benchmark"
      >
        U.S. Average at age 65: 56%<sup>1</sup>
      </p>
    </div>
  );
}

export function ColumnLtcAtAge() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcAtAge, ltcAtAgeShowRange } = useSelector(selectAppliedInferenceSet);
  const client = useSelector(selectClient);

  if (ltcAtAgeShowRange) {
    return null;
  }

  return (
    <div
      className="flex-grow-1 border-primary-200 flex basis-1/2 flex-col justify-between rounded-2xl border p-6 text-center"
      data-testid="predictions-age-column"
    >
      <p
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predictions-age-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} care is expected to begin at
        <SlideOutSheet
          slideOutContent={<LtcAtAgeSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="size-5 pb-1 pl-1 text-gray-500 hover:text-gray-700"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </p>
      <p
        className="text-primary-dark py-4 text-5xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predictions-age-value"
      >
        {ltcAtAge}
      </p>
      <p className="flex justify-center gap-2 text-base font-normal text-gray-700 md:text-xl">
        <span data-testid="predictions-age-unit">years old</span>
        {isInferenceEdited(client, 'ltcAtAge') && (
          <Badge
            color="gray"
            label="edited"
          />
        )}
      </p>
    </div>
  );
}

function ColumnLtcAgeFallBack() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcAtAge, ltcAtAgeShowRange } = useSelector(selectAppliedInferenceSet);
  const client = useSelector(selectClient);

  if (!ltcAtAgeShowRange) {
    return null;
  }

  return (
    <div className="flex-grow-1 border-primary-200 flex basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6">
      <p className="text-base font-medium text-gray-800 md:text-xl">
        {startCase(possessivePronoun(intakeSurvey))} care is expected to begin at{' '}
        <sup className="font-normal text-gray-700">2</sup>
      </p>
      <p className="text-primary-dark py-4 text-5xl font-semibold leading-[1.5] md:text-9xl">
        {ltcAtAge}
      </p>
      <p className="flex justify-center gap-2 text-base font-normal text-gray-700 md:text-xl">
        <span>years old</span>
        {isInferenceEdited(client, 'ltcAtAge') && (
          <Badge
            color="gray"
            label="edited"
          />
        )}
      </p>
    </div>
  );
}
